import {
  IconApi,
  IconArchive,
  IconBell,
  IconBrandGitlab,
  IconBrandSlack,
  IconChartBar,
  IconCloudUpload,
  IconContainer,
  IconDatabase,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconExternalLink,
  IconFile,
  IconLock,
  IconMail,
  IconPackage,
  IconPlayerPlay,
  IconServer,
  IconShield,
  IconTerminal2,
  IconTimeline,
  IconTriangle,
  IconUsers,
} from "@repo/ui/icons";
import type { ComponentType } from "react";

/* ═══════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════ */

export type LayerKey = "all" | "auth" | "cicd" | "data" | "incident";

export type NodeColorTheme = "blue" | "amber" | "teal" | "violet" | "gray" | "green";

export type DomainDef = {
  id: string;
  cls: string;
  label: string;
  badge: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

export type SubgroupDef = {
  id: string;
  domainId: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

export type NodeDef = {
  id: string;
  subgroupId?: string;
  domainId?: string;
  color: NodeColorTheme;
  icon: ComponentType<{ size?: number; stroke?: number }>;
  label: string;
  desc: string;
  x: number;
  y: number;
  layers: LayerKey[];
};

export type ArrowDef = {
  from: string;
  to: string;
  color: string;
  layers: LayerKey[];
  label: string;
};

/* ═══════════════════════════════════════════════
   CANVAS DIMENSIONS
═══════════════════════════════════════════════ */

export const CW = 1500;
export const CH = 580;
export const NW = 84;
export const NH = 84;

/* ═══════════════════════════════════════════════
   DATA
═══════════════════════════════════════════════ */

export const FILTER_BUTTONS: { key: LayerKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "auth", label: "Auth & Identity" },
  { key: "cicd", label: "CI/CD" },
  { key: "data", label: "Data & Storage" },
  { key: "incident", label: "Incident Mgmt" },
];

export const LEGEND_ITEMS: { label: string; color: string }[] = [
  { label: "Null Autos", color: "var(--mantine-color-blue-5)" },
  { label: "GitLab", color: "var(--mantine-color-orange-5)" },
  { label: "Ext. Null Autos", color: "var(--mantine-color-cyan-5)" },
  { label: "Customer", color: "var(--mantine-color-violet-5)" },
];

export const DOMAINS: DomainDef[] = [
  {
    id: "dn",
    cls: "domainBlue",
    label: "Null Autos Platform",
    badge: "",
    x: 20,
    y: 20,
    w: 772,
    h: 540,
  },
  {
    id: "dg",
    cls: "domainAmber",
    label: "GitLab",
    badge: "",
    x: 812,
    y: 20,
    w: 668,
    h: 170,
  },
  {
    id: "de",
    cls: "domainTeal",
    label: "External Null Autos Infrastructure",
    badge: "",
    x: 812,
    y: 210,
    w: 668,
    h: 150,
  },
  {
    id: "dc",
    cls: "domainViolet",
    label: "Customer Infrastructure",
    badge: "",
    x: 812,
    y: 380,
    w: 668,
    h: 180,
  },
];

export const SUBGROUPS: SubgroupDef[] = [
  { id: "sg-con", domainId: "dn", label: "Console", x: 30, y: 89, w: 116, h: 132 },
  { id: "sg-cp", domainId: "dn", label: "Control Plane", x: 162, y: 89, w: 216, h: 132 },
  { id: "sg-snap", domainId: "dn", label: "Snapshots", x: 394, y: 89, w: 216, h: 132 },
  { id: "sg-emu", domainId: "dn", label: "Emulators", x: 626, y: 89, w: 116, h: 132 },
  { id: "sg-s3", domainId: "dn", label: "Block Storage", x: 30, y: 237, w: 316, h: 132 },
  {
    id: "sg-gli",
    domainId: "dn",
    label: "GitLab Infrastructure",
    x: 362,
    y: 237,
    w: 380,
    h: 132,
  },
  { id: "sg-obs", domainId: "dn", label: "Observability", x: 30, y: 385, w: 416, h: 132 },
  { id: "sg-auth", domainId: "dn", label: "Auth", x: 462, y: 385, w: 116, h: 132 },
];

export const NODES: NodeDef[] = [
  // Console
  {
    id: "webconsole",
    subgroupId: "sg-con",
    color: "blue",
    icon: IconDeviceDesktop,
    label: "Web Console",
    desc: "Browser UX — primary interface for engineers to interact with emulators and remote workspaces.",
    x: 16,
    y: 32,
    layers: ["all", "cicd", "auth"],
  },
  // Control Plane
  {
    id: "cpdb",
    subgroupId: "sg-cp",
    color: "blue",
    icon: IconDatabase,
    label: "Database",
    desc: "Persistent state for orgs, projects, emulator metadata, and snapshot refs.",
    x: 16,
    y: 32,
    layers: ["all", "data"],
  },
  {
    id: "cpapi",
    subgroupId: "sg-cp",
    color: "blue",
    icon: IconApi,
    label: "API",
    desc: "Control Plane API — orchestrates emulator pods, snapshot lifecycle, and enforces RBAC.",
    x: 116,
    y: 32,
    layers: ["all", "cicd", "auth", "data"],
  },
  // Snapshots
  {
    id: "snapctrl",
    subgroupId: "sg-snap",
    color: "blue",
    icon: IconArchive,
    label: "Snapshot Controller",
    desc: "API server for snapshot lifecycle — create, restore, delete.",
    x: 16,
    y: 32,
    layers: ["all", "cicd", "data"],
  },
  {
    id: "snapdp",
    subgroupId: "sg-snap",
    color: "blue",
    icon: IconServer,
    label: "Data Plane",
    desc: "Disk snapshot tree — AAOS image layers with clone-on-write for fast emulator launches.",
    x: 116,
    y: 32,
    layers: ["all", "cicd", "data"],
  },
  // Emulators
  {
    id: "emulators",
    subgroupId: "sg-emu",
    color: "blue",
    icon: IconDeviceMobile,
    label: "Emulators",
    desc: "Android Automotive OS emulators running in Kubernetes pods.",
    x: 16,
    y: 32,
    layers: ["all", "cicd"],
  },
  // Block Storage
  {
    id: "s3cpbk",
    subgroupId: "sg-s3",
    color: "gray",
    icon: IconDatabase,
    label: "CP DB Backups",
    desc: "Encrypted backups — nightly backups of Control Plane database.",
    x: 16,
    y: 32,
    layers: ["all", "data"],
  },
  {
    id: "s3snapbk",
    subgroupId: "sg-s3",
    color: "gray",
    icon: IconCloudUpload,
    label: "Snapshot Backups",
    desc: "Encrypted backups — backup of snapshot data plane disk trees.",
    x: 116,
    y: 32,
    layers: ["all", "data"],
  },
  {
    id: "s3infra",
    subgroupId: "sg-s3",
    color: "gray",
    icon: IconFile,
    label: "Infra Config & State",
    desc: "Terraform state, Helm values, and infrastructure configuration.",
    x: 216,
    y: 32,
    layers: ["all", "data"],
  },
  // GitLab Infrastructure
  {
    id: "workspaces",
    subgroupId: "sg-gli",
    color: "amber",
    icon: IconDeviceDesktop,
    label: "Workspaces",
    desc: "Remote dev environments per-engineer.",
    x: 16,
    y: 32,
    layers: ["all", "cicd", "auth"],
  },
  {
    id: "wsproxy",
    subgroupId: "sg-gli",
    color: "amber",
    icon: IconExternalLink,
    label: "Workspaces Proxy",
    desc: "Proxy server for remote workspace routing — terminates TLS and forwards dev traffic.",
    x: 100,
    y: 32,
    layers: ["all", "cicd"],
  },
  {
    id: "agent",
    subgroupId: "sg-gli",
    color: "amber",
    icon: IconTimeline,
    label: "Agent",
    desc: "GitLab Agent for Kubernetes — connects EKS cluster to GitLab.",
    x: 192,
    y: 32,
    layers: ["all", "cicd"],
  },
  {
    id: "runners",
    subgroupId: "sg-gli",
    color: "amber",
    icon: IconPlayerPlay,
    label: "Runners",
    desc: "Custom hosted GitLab CI runners — execute pipelines, access emulators and snapshot data plane.",
    x: 280,
    y: 32,
    layers: ["all", "cicd", "data"],
  },
  // Observability
  {
    id: "logs",
    subgroupId: "sg-obs",
    color: "green",
    icon: IconTerminal2,
    label: "Logs",
    desc: "Centralized log aggregation for platform services.",
    x: 16,
    y: 32,
    layers: ["all", "incident"],
  },
  {
    id: "telemetry",
    subgroupId: "sg-obs",
    color: "green",
    icon: IconTimeline,
    label: "Telemetry",
    desc: "OpenTelemetry traces exported to a centralized tracing backend.",
    x: 116,
    y: 32,
    layers: ["all", "incident"],
  },
  {
    id: "metrics",
    subgroupId: "sg-obs",
    color: "green",
    icon: IconChartBar,
    label: "Metrics",
    desc: "Infrastructure and application metrics.",
    x: 216,
    y: 32,
    layers: ["all", "incident"],
  },
  {
    id: "alerts",
    subgroupId: "sg-obs",
    color: "green",
    icon: IconBell,
    label: "Alerts",
    desc: "Alerting rules — fires to Rootly on threshold breach to trigger incident creation.",
    x: 316,
    y: 32,
    layers: ["all", "incident"],
  },
  // Auth
  {
    id: "auth",
    subgroupId: "sg-auth",
    color: "blue",
    icon: IconShield,
    label: "Auth",
    desc: "Multi-tenant AuthN/AuthZ. OIDC IdP to GitLab. Enforces RBAC on API, Console, Workspaces.",
    x: 16,
    y: 32,
    layers: ["all", "auth"],
  },
  // GitLab Account
  {
    id: "gitlab",
    domainId: "dg",
    color: "amber",
    icon: IconBrandGitlab,
    label: "GitLab",
    desc: "GitLab instance (Dedicated or Self-Hosted) — source of truth for code, CI/CD pipelines, MRs.",
    x: 16,
    y: 70,
    layers: ["all", "cicd"],
  },
  {
    id: "pkgreg",
    domainId: "dg",
    color: "amber",
    icon: IconPackage,
    label: "Package Registry",
    desc: "GitLab Package Registry — build artifacts, APKs, and library packages.",
    x: 116,
    y: 70,
    layers: ["all", "cicd"],
  },
  {
    id: "ctrreg",
    domainId: "dg",
    color: "amber",
    icon: IconContainer,
    label: "Container Registry",
    desc: "GitLab Container Registry — Docker images for AAOS builds and platform services.",
    x: 216,
    y: 70,
    layers: ["all", "cicd"],
  },
  {
    id: "groupapp",
    domainId: "dg",
    color: "amber",
    icon: IconUsers,
    label: "Group Application",
    desc: "GitLab Group Application — OAuth/OIDC app. Auth service uses it as identity provider for SSO.",
    x: 316,
    y: 70,
    layers: ["all", "auth"],
  },
  {
    id: "servicedesk",
    domainId: "dg",
    color: "amber",
    icon: IconMail,
    label: "Service Desk",
    desc: "GitLab Service Desk — customer support ticketing linked to issues.",
    x: 440,
    y: 70,
    layers: ["all", "incident"],
  },
  {
    id: "sso",
    domainId: "dg",
    color: "amber",
    icon: IconLock,
    label: "SSO",
    desc: "GitLab SSO — federates with customer Identity Provider via SAML/OIDC.",
    x: 564,
    y: 70,
    layers: ["all", "auth"],
  },
  // External Null Autos
  {
    id: "rootly",
    domainId: "de",
    color: "teal",
    icon: IconTriangle,
    label: "Rootly",
    desc: "Incident management — triggered by Alerts, creates incidents, routes notifications.",
    x: 16,
    y: 50,
    layers: ["all", "incident"],
  },
  {
    id: "slack",
    domainId: "de",
    color: "teal",
    icon: IconBrandSlack,
    label: "Slack",
    desc: "Internal ops notifications from Rootly on incident creation and updates.",
    x: 116,
    y: 50,
    layers: ["all", "incident"],
  },
  {
    id: "pubreg",
    domainId: "de",
    color: "teal",
    icon: IconContainer,
    label: "Public Registry",
    desc: "Public registry for base images referenced by CI pipelines.",
    x: 216,
    y: 50,
    layers: ["all", "cicd"],
  },
  {
    id: "nullctl",
    domainId: "de",
    color: "teal",
    icon: IconTerminal2,
    label: "nullctl",
    desc: "CLI — engineers interact with Null Autos infrastructure and Control Plane API.",
    x: 316,
    y: 50,
    layers: ["all"],
  },
  // Customer
  {
    id: "idp",
    domainId: "dc",
    color: "violet",
    icon: IconShield,
    label: "Identity Provider",
    desc: "Customer-owned IdP — federated into GitLab SSO via SAML/OIDC.",
    x: 16,
    y: 70,
    layers: ["all", "auth"],
  },
];

export const ARROWS: ArrowDef[] = [
  // Control plane internals
  {
    from: "cpdb",
    to: "cpapi",
    color: "var(--mantine-color-blue-5)",
    layers: ["all", "data"],
    label: "reads/writes",
  },
  {
    from: "snapctrl",
    to: "cpapi",
    color: "var(--mantine-color-blue-5)",
    layers: ["all", "cicd"],
    label: "orchestrate",
  },
  {
    from: "snapctrl",
    to: "snapdp",
    color: "var(--mantine-color-blue-5)",
    layers: ["all", "data"],
    label: "disk ops",
  },
  {
    from: "webconsole",
    to: "emulators",
    color: "var(--mantine-color-blue-5)",
    layers: ["all", "cicd"],
    label: "stream",
  },
  {
    from: "webconsole",
    to: "cpapi",
    color: "var(--mantine-color-blue-5)",
    layers: ["all", "cicd"],
    label: "API calls",
  },
  {
    from: "emulators",
    to: "cpapi",
    color: "var(--mantine-color-blue-5)",
    layers: ["all", "cicd"],
    label: "status",
  },
  // Runners
  {
    from: "runners",
    to: "cpapi",
    color: "var(--mantine-color-orange-5)",
    layers: ["all", "cicd"],
    label: "API calls",
  },
  {
    from: "runners",
    to: "snapdp",
    color: "var(--mantine-color-orange-5)",
    layers: ["all", "cicd", "data"],
    label: "snapshot ops",
  },
  {
    from: "runners",
    to: "emulators",
    color: "var(--mantine-color-orange-5)",
    layers: ["all", "cicd"],
    label: "ADB / test",
  },
  {
    from: "runners",
    to: "agent",
    color: "var(--mantine-color-orange-5)",
    layers: ["all", "cicd"],
    label: "k8s ops",
  },
  // Workspaces
  {
    from: "workspaces",
    to: "webconsole",
    color: "var(--mantine-color-orange-5)",
    layers: ["all", "cicd"],
    label: "IDE bridge",
  },
  {
    from: "workspaces",
    to: "snapdp",
    color: "var(--mantine-color-orange-5)",
    layers: ["all", "cicd", "data"],
    label: "snapshot mount",
  },
  {
    from: "wsproxy",
    to: "workspaces",
    color: "var(--mantine-color-orange-5)",
    layers: ["all", "cicd"],
    label: "proxy",
  },
  // GitLab → runners
  {
    from: "gitlab",
    to: "runners",
    color: "var(--mantine-color-orange-7)",
    layers: ["all", "cicd"],
    label: "CI jobs",
  },
  {
    from: "pkgreg",
    to: "runners",
    color: "var(--mantine-color-orange-7)",
    layers: ["all", "cicd"],
    label: "artifacts",
  },
  {
    from: "ctrreg",
    to: "runners",
    color: "var(--mantine-color-orange-7)",
    layers: ["all", "cicd"],
    label: "images",
  },
  {
    from: "agent",
    to: "gitlab",
    color: "var(--mantine-color-orange-5)",
    layers: ["all", "cicd"],
    label: "cluster info",
  },
  {
    from: "wsproxy",
    to: "gitlab",
    color: "var(--mantine-color-orange-5)",
    layers: ["all", "cicd"],
    label: "ws mgmt",
  },
  {
    from: "sso",
    to: "groupapp",
    color: "var(--mantine-color-orange-7)",
    layers: ["all", "auth"],
    label: "federation",
  },
  // Auth flows
  {
    from: "auth",
    to: "groupapp",
    color: "var(--mantine-color-cyan-5)",
    layers: ["all", "auth"],
    label: "OIDC IdP",
  },
  {
    from: "auth",
    to: "cpapi",
    color: "var(--mantine-color-cyan-5)",
    layers: ["all", "auth"],
    label: "enforce AuthZ",
  },
  {
    from: "auth",
    to: "webconsole",
    color: "var(--mantine-color-cyan-5)",
    layers: ["all", "auth"],
    label: "session",
  },
  {
    from: "auth",
    to: "workspaces",
    color: "var(--mantine-color-cyan-5)",
    layers: ["all", "auth"],
    label: "token",
  },
  // Customer IdP
  {
    from: "idp",
    to: "sso",
    color: "var(--mantine-color-violet-5)",
    layers: ["all", "auth"],
    label: "SAML/OIDC",
  },
  // Incident
  {
    from: "alerts",
    to: "rootly",
    color: "var(--mantine-color-green-5)",
    layers: ["all", "incident"],
    label: "fire alert",
  },
  {
    from: "rootly",
    to: "slack",
    color: "var(--mantine-color-green-5)",
    layers: ["all", "incident"],
    label: "notify",
  },
  {
    from: "servicedesk",
    to: "rootly",
    color: "var(--mantine-color-orange-7)",
    layers: ["all", "incident"],
    label: "incidents",
  },
  // Backups
  {
    from: "cpdb",
    to: "s3cpbk",
    color: "var(--mantine-color-gray-6)",
    layers: ["all", "data"],
    label: "backup",
  },
  {
    from: "snapdp",
    to: "s3snapbk",
    color: "var(--mantine-color-gray-6)",
    layers: ["all", "data"],
    label: "backup",
  },
  // nullctl
  {
    from: "nullctl",
    to: "cpapi",
    color: "var(--mantine-color-cyan-5)",
    layers: ["all"],
    label: "CLI",
  },
  {
    from: "nullctl",
    to: "s3infra",
    color: "var(--mantine-color-cyan-5)",
    layers: ["all", "data"],
    label: "config",
  },
  // Public registry
  {
    from: "pubreg",
    to: "runners",
    color: "var(--mantine-color-cyan-5)",
    layers: ["all", "cicd"],
    label: "base images",
  },
];

/* ═══════════════════════════════════════════════
   LAYOUT HELPERS
═══════════════════════════════════════════════ */

export const domainMap = new Map(DOMAINS.map((d) => [d.id, d]));
export const subgroupMap = new Map(SUBGROUPS.map((sg) => [sg.id, sg]));
export const nodeMap = new Map(NODES.map((n) => [n.id, n]));

export function absPos(n: NodeDef): { x: number; y: number } {
  if (n.subgroupId) {
    const sg = subgroupMap.get(n.subgroupId)!;
    const dom = domainMap.get(sg.domainId)!;
    return { x: dom.x + sg.x + n.x, y: dom.y + sg.y + n.y };
  }
  if (n.domainId) {
    const dom = domainMap.get(n.domainId)!;
    return { x: dom.x + n.x, y: dom.y + n.y };
  }
  return { x: n.x, y: n.y };
}

export function centre(n: NodeDef): { x: number; y: number } {
  const p = absPos(n);
  return { x: p.x + NW / 2, y: p.y + NH / 2 };
}

export function edgePt(
  cx: number,
  cy: number,
  tx: number,
  ty: number,
): { x: number; y: number } {
  const hw = NW / 2;
  const hh = NH / 2;
  const dx = tx - cx;
  const dy = ty - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };
  const tx2 = dx === 0 ? Infinity : hw / Math.abs(dx);
  const ty2 = dy === 0 ? Infinity : hh / Math.abs(dy);
  const t = Math.min(tx2, ty2);
  return { x: cx + dx * t, y: cy + dy * t };
}
